import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { LiveKitRoom } from '@livekit/components-react';
import { Room } from 'livekit-client';

import { IUser } from '../../lib/interfaces';
import { reportError } from '../../lib/sentry';
import { VideoToken } from '../../lib/services/videoService';
import { isMediaDeviceError, rememberDeviceErrors } from './callSignals';
import { DS } from './designTokens';
import CallFailed from './components/CallFailed';
import CallInProgress from './CallInProgress';
import { CallPhase } from './types';
import VideoCallHeader from './components/VideoCallHeader';

const failureMessageKey = (phase: CallPhase): string => {
  if (phase === 'disconnected') return 'videoVisit.status.disconnected';
  return 'videoVisit.error.connection';
};

type VideoCallProps = {
  credentials: VideoToken;
  patient: IUser;
  onRetry: () => void;
  onLeave: () => void;
};

const VideoCall = ({
  credentials,
  patient,
  onRetry,
  onLeave,
}: VideoCallProps) => {
  const [phase, setPhase] = useState<CallPhase>('connecting');
  // Created here rather than inside LiveKitRoom so this component can listen
  // for device errors. LiveKitRoom's connect effect runs before this one, but
  // LiveKit only starts the camera and mic on SignalConnected, which arrives
  // after the listener is attached.
  const [room] = useState(() => new Room());
  useEffect(() => rememberDeviceErrors(room), [room]);

  const handleConnectionError = useCallback((err: Error) => {
    if (isMediaDeviceError(err)) return;
    reportError(err, { tags: { feature: 'video' } });
    // A failure after connecting is surfaced by onDisconnected, which keeps
    // the call screen up through LiveKit's own reconnect attempts.
    setPhase((current) => (current === 'connected' ? current : 'error'));
  }, []);

  const isLive = phase === 'connecting' || phase === 'connected';

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: DS.linen,
      }}
    >
      <VideoCallHeader patient={patient} phase={phase} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          p: '20px',
        }}
      >
        {isLive ? (
          <LiveKitRoom
            room={room}
            serverUrl={credentials.url}
            token={credentials.token}
            connect
            audio
            video
            onConnected={() => setPhase('connected')}
            onDisconnected={() =>
              setPhase((current) =>
                current === 'error' ? current : 'disconnected'
              )
            }
            onError={handleConnectionError}
            style={{ flex: 1, display: 'flex', minHeight: 0 }}
          >
            <CallInProgress patient={patient} onLeave={onLeave} />
          </LiveKitRoom>
        ) : (
          <CallFailed
            messageKey={failureMessageKey(phase)}
            onRetry={onRetry}
            onClose={onLeave}
          />
        )}
      </Box>
    </Box>
  );
};

export default VideoCall;
